import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import { Slider } from "@/components/ui/slider";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const EMERALD = "#0e5b45";

const fmt = (n) =>
  "\u20B9" +
  Math.round(n).toLocaleString("en-IN", { maximumFractionDigits: 0 });

const fmtShort = (n) => {
  if (n >= 10000000) return "\u20B9" + (n / 10000000).toFixed(2) + " Cr";
  if (n >= 100000) return "\u20B9" + (n / 100000).toFixed(1) + " L";
  if (n >= 1000) return "\u20B9" + (n / 1000).toFixed(0) + "K";
  return "\u20B9" + Math.round(n);
};

function ControlRow({ label, value, display, min, max, step, onChange, testid }) {
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <label className="text-sm text-stone-600">{label}</label>
        <span className="text-[#0e5b45] font-semibold tabular-nums">{display}</span>
      </div>
      <Slider
        className="gold-slider"
        value={[value]}
        min={min}
        max={max}
        step={step}
        onValueChange={(v) => onChange(v[0])}
        data-testid={testid}
      />
    </div>
  );
}

function ChartBlock({ data, investedKey = "invested", valueKey = "value" }) {
  return (
    <div className="h-64 w-full" data-testid="calc-chart">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 10, right: 6, left: 6, bottom: 0 }}>
          <defs>
            <linearGradient id="gVal" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={EMERALD} stopOpacity={0.35} />
              <stop offset="100%" stopColor={EMERALD} stopOpacity={0} />
            </linearGradient>
            <linearGradient id="gInv" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#b8925a" stopOpacity={0.3} />
              <stop offset="100%" stopColor="#b8925a" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.07)" vertical={false} />
          <XAxis dataKey="year" tick={{ fill: "#78716c", fontSize: 12 }} tickLine={false} axisLine={false} />
          <YAxis tickFormatter={fmtShort} tick={{ fill: "#78716c", fontSize: 11 }} tickLine={false} axisLine={false} width={54} />
          <Tooltip
            contentStyle={{
              background: "#ffffff",
              border: "1px solid rgba(0,0,0,0.12)",
              borderRadius: 8,
              color: "#1a1a17",
            }}
            formatter={(v, name) => [fmt(v), name === valueKey ? "Value" : "Invested"]}
            labelFormatter={(l) => `Year ${l}`}
          />
          <Area type="monotone" dataKey={investedKey} stroke="#b8925a" strokeWidth={1.5} fill="url(#gInv)" />
          <Area type="monotone" dataKey={valueKey} stroke={EMERALD} strokeWidth={2.5} fill="url(#gVal)" />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

function Summary({ items }) {
  return (
    <div className="grid grid-cols-3 gap-4 mt-8">
      {items.map((it) => (
        <div key={it.label} className="border-l border-black/10 pl-4">
          <p className="text-xs text-stone-500 uppercase tracking-wider">{it.label}</p>
          <p className={`mt-2 text-lg md:text-xl font-semibold tabular-nums ${it.highlight ? "text-[#0e5b45]" : "text-[#1a1a17]"}`}>
            {it.value}
          </p>
        </div>
      ))}
    </div>
  );
}

function Sip() {
  const [amount, setAmount] = useState(10000);
  const [rate, setRate] = useState(12);
  const [years, setYears] = useState(15);

  const { data, invested, future } = useMemo(() => {
    const i = rate / 100 / 12;
    const rows = [];
    let inv = 0;
    let fv = 0;
    for (let y = 1; y <= years; y++) {
      const n = y * 12;
      fv = amount * (((Math.pow(1 + i, n) - 1) / i) * (1 + i));
      inv = amount * n;
      rows.push({ year: y, invested: inv, value: Math.round(fv) });
    }
    return { data: rows, invested: amount * years * 12, future: fv };
  }, [amount, rate, years]);

  return (
    <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
      <div className="space-y-8">
        <ControlRow label="Monthly Investment" value={amount} display={fmt(amount)} min={500} max={200000} step={500} onChange={setAmount} testid="sip-amount" />
        <ControlRow label="Expected Return (p.a.)" value={rate} display={`${rate}%`} min={5} max={20} step={0.5} onChange={setRate} testid="sip-rate" />
        <ControlRow label="Time Period" value={years} display={`${years} yrs`} min={1} max={35} step={1} onChange={setYears} testid="sip-years" />
        <Summary
          items={[
            { label: "Invested", value: fmtShort(invested) },
            { label: "Returns", value: fmtShort(future - invested) },
            { label: "Total Value", value: fmtShort(future), highlight: true },
          ]}
        />
      </div>
      <div><ChartBlock data={data} /></div>
    </div>
  );
}

function Lumpsum() {
  const [amount, setAmount] = useState(500000);
  const [rate, setRate] = useState(12);
  const [years, setYears] = useState(10);

  const { data, future } = useMemo(() => {
    const rows = [];
    let fv = amount;
    for (let y = 1; y <= years; y++) {
      fv = amount * Math.pow(1 + rate / 100, y);
      rows.push({ year: y, invested: amount, value: Math.round(fv) });
    }
    return { data: rows, future: fv };
  }, [amount, rate, years]);

  return (
    <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
      <div className="space-y-8">
        <ControlRow label="Investment Amount" value={amount} display={fmt(amount)} min={10000} max={10000000} step={10000} onChange={setAmount} testid="lump-amount" />
        <ControlRow label="Expected Return (p.a.)" value={rate} display={`${rate}%`} min={5} max={20} step={0.5} onChange={setRate} testid="lump-rate" />
        <ControlRow label="Time Period" value={years} display={`${years} yrs`} min={1} max={35} step={1} onChange={setYears} testid="lump-years" />
        <Summary
          items={[
            { label: "Invested", value: fmtShort(amount) },
            { label: "Returns", value: fmtShort(future - amount) },
            { label: "Total Value", value: fmtShort(future), highlight: true },
          ]}
        />
      </div>
      <div><ChartBlock data={data} /></div>
    </div>
  );
}

function Goal() {
  const [target, setTarget] = useState(10000000);
  const [rate, setRate] = useState(12);
  const [years, setYears] = useState(20);

  const { data, monthly } = useMemo(() => {
    const i = rate / 100 / 12;
    const n = years * 12;
    const req = target / (((Math.pow(1 + i, n) - 1) / i) * (1 + i));
    const rows = [];
    for (let y = 1; y <= years; y++) {
      const m = y * 12;
      const fv = req * (((Math.pow(1 + i, m) - 1) / i) * (1 + i));
      rows.push({ year: y, invested: req * m, value: Math.round(fv) });
    }
    return { data: rows, monthly: req };
  }, [target, rate, years]);

  return (
    <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
      <div className="space-y-8">
        <ControlRow label="Target Corpus" value={target} display={fmtShort(target)} min={500000} max={100000000} step={500000} onChange={setTarget} testid="goal-target" />
        <ControlRow label="Expected Return (p.a.)" value={rate} display={`${rate}%`} min={5} max={20} step={0.5} onChange={setRate} testid="goal-rate" />
        <ControlRow label="Years to Goal" value={years} display={`${years} yrs`} min={1} max={35} step={1} onChange={setYears} testid="goal-years" />
        <Summary
          items={[
            { label: "Goal", value: fmtShort(target) },
            { label: "Duration", value: `${years} yrs` },
            { label: "Monthly SIP", value: fmt(monthly), highlight: true },
          ]}
        />
      </div>
      <div><ChartBlock data={data} /></div>
    </div>
  );
}

export default function Calculators() {
  return (
    <section id="calculators" className="bg-base py-24 md:py-36" data-testid="calculators">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="mb-14 max-w-2xl"
        >
          <p className="overline mb-5">Plan With Precision</p>
          <h2 className="font-serif text-4xl md:text-5xl text-[#1a1a17] tracking-tight">
            See your money <span className="italic text-[#0e5b45]">compound</span>
          </h2>
          <p className="text-stone-600 mt-5 md:text-lg">
            Interactive calculators to visualise the future value of your investments. Adjust the sliders and watch compounding at work.
          </p>
        </motion.div>

        <Tabs defaultValue="sip" className="w-full">
          <TabsList className="bg-[#efe9de] border border-black/10 mb-12 p-1 h-auto rounded-full inline-flex">
            <TabsTrigger value="sip" data-testid="tab-sip" className="rounded-full px-6 py-2.5 data-[state=active]:bg-[#0e5b45] data-[state=active]:text-white text-stone-600">SIP</TabsTrigger>
            <TabsTrigger value="lumpsum" data-testid="tab-lumpsum" className="rounded-full px-6 py-2.5 data-[state=active]:bg-[#0e5b45] data-[state=active]:text-white text-stone-600">Lumpsum</TabsTrigger>
            <TabsTrigger value="goal" data-testid="tab-goal" className="rounded-full px-6 py-2.5 data-[state=active]:bg-[#0e5b45] data-[state=active]:text-white text-stone-600">Goal Planner</TabsTrigger>
          </TabsList>

          <div className="bg-white border border-black/10 rounded-lg p-8 md:p-12 shadow-[0_1px_40px_rgba(26,26,23,0.05)]">
            <TabsContent value="sip"><Sip /></TabsContent>
            <TabsContent value="lumpsum"><Lumpsum /></TabsContent>
            <TabsContent value="goal"><Goal /></TabsContent>
          </div>
        </Tabs>
        <p className="text-xs text-stone-500 mt-6 max-w-2xl">
          Illustrative projections based on assumed constant returns. Actual mutual fund returns vary and are subject to market risk.
        </p>
      </div>
    </section>
  );
}
